/**
 * FRONT-1003: the Work board's aggregate read — one row per project
 * container (FRONT-2378 retired `routine` as a container kind) with
 * everything the board's cards + stat strip need already computed
 * server-side (task counts, next open task, people, a context snippet,
 * last-activity). Replaces the old flat `useGraphEntities` read the board
 * used when it was just name+chevron rows — the new cards need richer
 * per-item data than a plain entity list carries.
 *
 * `fetchWorkBoard` is exported separately from the hook so the wire shape is
 * unit-testable without a QueryClient, mirroring `fetchWorkTasks` in
 * `use-work-tasks.ts`.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { workBoardKeys } from '~/lib/query-keys';

export type WorkBoardItemType = 'project';

export interface WorkBoardPerson {
  id: string;
  name: string;
  role: string | null;
}

export interface WorkBoardNextTask {
  /** FRONT-2423: lets the board card open this task's detail window. */
  id: string;
  title: string;
  /** YYYY-MM-DD, or null when unset. Never round-tripped through `Date`. */
  dueDate: string | null;
}

export interface WorkBoardItem {
  id: string;
  name: string;
  type: WorkBoardItemType;
  /** Non-null → the container is on the Inactive view (soft-archived, FRONT-892). */
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lastActivityAt: string;
  openTaskCount: number;
  doneTaskCount: number;
  overdueOpenTaskCount: number;
  nextOpenTask: WorkBoardNextTask | null;
  people: WorkBoardPerson[];
  peopleCount: number;
  /** The FULL distinct set of person ids for this container — ids only,
   *  uncapped (unlike `people`, capped at 12 server-side). Use this, not
   *  `people`, when unioning people across containers (e.g. the board's
   *  header "People" stat) so counts past the cap aren't undercounted. */
  peopleIds: string[];
  contextSnippet: string | null;
  /** FRONT-1339 follow-up: true for exactly the caller's marked Unsorted
   *  holding container — the one place dictated notes land when Eddy isn't
   *  confident which project they belong to. Membership is a backend marker
   *  attribute, never the (renameable, display-only) name, so the frontend
   *  must key its card treatment off this flag, not `item.name`. When true
   *  AND the container is an active `project`, the backend also pins it
   *  first in the board's own ordering — see `board-store.ts`. */
  isUnsorted: boolean;
  /** FRONT-1339 follow-up: how many notes are filed directly in this
   *  container — the Unsorted tray's headline "drain me" signal, but
   *  computed for every container. */
  noteCount: number;
  /** FRONT-1085: how many Files-screen items (uploads or saved documents)
   *  are attached to this container — counts attachment LINK rows, not
   *  live/resolvable targets, so a card's count and the detail page's Files
   *  panel (which also shows unavailable rows) never disagree. */
  fileCount: number;
  /** FRONT-1439: how many whole Files-screen FOLDERS are attached to this
   *  container. Counted separately from `fileCount` — a folder's own member
   *  files are NOT counted into `fileCount`, so the two never double-count
   *  the same underlying material. */
  folderCount: number;
  /** FRONT-1802: how many sketches are attached to this container. Counted
   *  separately from `fileCount`/`folderCount`, same reasoning — a sketch
   *  is its own attachment kind, never double-counted into either. */
  sketchCount: number;
  /** FRONT-2633: null for the caller's own project; for one a colleague
   *  shared with them, their role and whose it is. A shared card carries no
   *  people — those live in the owner's private memory. */
  share: { role: 'editor' | 'viewer'; ownerUserId: string; ownerName: string | null } | null;
}

interface WorkBoardResponse {
  items: WorkBoardItem[];
}

export function fetchWorkBoard(): Promise<WorkBoardResponse> {
  return api.get<WorkBoardResponse>('/api/work/board');
}

/**
 * One unfiltered fetch of the whole board (no pagination) — the page filters
 * by view (active/inactive) client-side, and derives the stat strip from the
 * same set. `items` defaults to `[]` so callers don't need an `?? []` at
 * every use site.
 *
 * `enabled` (FRONT-1339 code-review) defaults to `true` — unchanged for the
 * Work board page, which wants the fetch immediately. `ContainerPicker`
 * passes `enabled: isOpen` instead: it mounts per-row (e.g. one per note in a
 * container's Notes list), and without this the full board aggregation —
 * task rollups, people, context snippets, cron automations — fired on every
 * mount just to populate a hover-revealed picker the user may never open.
 */
export function useWorkBoard(options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: workBoardKeys.all,
    queryFn: fetchWorkBoard,
    enabled: options?.enabled ?? true,
  });
  return {
    items: query.data?.items ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
